import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'pr' | 'journalist';
  outlet?: string;
  lastActive: number;
  mustChangePassword: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Demo accounts REMOVED - using real database only

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on app load
  useEffect(() => {
    const loadUserSession = async () => {
      console.log('AuthContext: Checking for existing session...');
      const savedUser = localStorage.getItem('fx-user');
      console.log('AuthContext: Found saved user?', !!savedUser);
      
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          console.log('AuthContext: Parsed saved user:', parsedUser);
          
          // Verify user still exists in database
          try {
            console.log('AuthContext: Verifying user in database...');
            const { data: userData, error } = await supabase
              .from('users')
              .select('*')
              .ilike('email', parsedUser.email)
              .single();

            if (!error && userData) {
              // Update with fresh database data
              const updatedUser: User = {
                id: userData.id,
                email: userData.email,
                name: userData.name,
                role: userData.role,
                outlet: userData.outlet,
                lastActive: Date.now(),
                mustChangePassword: userData.must_change_password || false
              };
              setUser(updatedUser);
              localStorage.setItem('fx-user', JSON.stringify(updatedUser));
            } else {
              // User no longer exists, clear session
              localStorage.removeItem('fx-user');
            }
          } catch (dbError) {
            console.error('Error checking user state from database:', dbError);
            // Clear invalid session
            localStorage.removeItem('fx-user');
          }
        } catch (error) {
          console.error('Error parsing saved user:', error);
          localStorage.removeItem('fx-user');
        }
      }
      setIsLoading(false);
    };

    loadUserSession();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Check database users only
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .ilike('email', email)
        .single();

      console.log('Login attempt for:', email);
      console.log('Email length:', email.length);
      console.log('Email char codes:', Array.from(email).map(c => c.charCodeAt(0)));
      console.log('Database query error:', error);
      console.log('User found:', !!userData);
      
      if (userData) {
        console.log('Found user email:', userData.email);
        console.log('Found user email length:', userData.email.length);
        console.log('Found user email char codes:', Array.from(userData.email).map(c => c.charCodeAt(0)));
      }

      if (error || !userData) {
        console.log('Login failed: User not found or database error');
        setIsLoading(false);
        return false;
      }

      // Check if password matches temp_password or password_hash
      const isValidPassword = userData.temp_password === password || userData.password_hash === password;
      
      console.log('Password validation:', {
        providedPassword: password,
        matchesTempPassword: userData.temp_password === password,
        matchesPasswordHash: userData.password_hash === password,
        isValid: isValidPassword
      });
      
      if (!isValidPassword) {
        console.log('Login failed: Invalid password');
        setIsLoading(false);
        return false;
      }

      // Determine if user must change password:
      // 1. If must_change_password flag is true in database
      // 2. If user is logging in with temp_password (first time login)
      const isUsingTempPassword = userData.temp_password === password;
      const mustChangePassword = userData.must_change_password || isUsingTempPassword;

      // Update last active timestamp
      await supabase
        .from('users')
        .update({ last_active: new Date().toISOString() })
        .eq('id', userData.id);

      const user: User = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        outlet: userData.outlet || undefined,
        lastActive: Date.now(),
        mustChangePassword: mustChangePassword
      };
      
      console.log('Setting user state:', user);
      setUser(user);
      
      try {
        localStorage.setItem('fx-user', JSON.stringify(user));
        console.log('User saved to localStorage successfully');
      } catch (e) {
        console.error('Failed to save user to localStorage:', e);
      }
      
      setIsLoading(false);
      console.log('Login successful, returning true');
      return true;
      
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('fx-user');
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Verify current password and update for all users
      const { data: userData, error } = await supabase
        .from('users')
        .select('temp_password, password_hash')
        .eq('id', user.id)
        .single();

      if (error || !userData) {
        return false;
      }

      // Verify current password
      const isCurrentPasswordValid = userData.temp_password === currentPassword || userData.password_hash === currentPassword;
      
      if (!isCurrentPasswordValid) {
        return false;
      }

      // Update password and reset temp password fields
      const { error: updateError } = await supabase
        .from('users')
        .update({
          password_hash: newPassword, // In production, this should be properly hashed
          temp_password: null,
          must_change_password: false,
          invitation_status: 'accepted'
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('Error updating password:', updateError);
        return false;
      }

      // Update local user state
      const updatedUser = { ...user, mustChangePassword: false };
      setUser(updatedUser);
      localStorage.setItem('fx-user', JSON.stringify(updatedUser));

      return true;
    } catch (error) {
      console.error('Change password error:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, changePassword, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};